import { routes } from '../../config/routes/routes';
import {
  CONFIRMED_ORDER_KEY,
  PAYMENT_IN_PROGRESS_KEY,
  PAYMENT_SUCCESS_STATUSES,
  PLACED_ORDER_KEY,
} from './payment.constants';

export type PlacedOrderItem = {
  sku: string;
  manufacturer: string;
  name: string;
  image?: string;
  quantity: number;
  // Unit price with VAT, as the basket showed it.
  price: number;
};

// The order as the checkout summary quoted it to the buyer - the same numbers
// the backend charges - so the thank-you page can repeat it without asking the
// API (the public order lookup has no small order fee and net item prices).
export type PlacedOrder = {
  orderId?: number;
  email?: string;
  items: PlacedOrderItem[];
  productsPrice: number;
  promoCode?: { name: string; discount: number };
  deliveryPrice?: number;
  smallOrderFee: number;
  total: number;
  vatRate: number;
  vatAmount: number;
  estimatedDelivery?: string;
  shippingMethod?: string;
  deliveryAddress?: string[];
};

// `order` is null when the payment came back but this tab never saw the order
// being placed: still a thank-you, just without the details.
export type ConfirmedOrder = { order: PlacedOrder | null };

const readJson = <T>(key: string): T | null => {
  try {
    const value = sessionStorage.getItem(key);

    return value ? (JSON.parse(value) as T) : null;
  } catch {
    return null;
  }
};

export const rememberPlacedOrder = (order: PlacedOrder) => {
  sessionStorage.setItem(PLACED_ORDER_KEY, JSON.stringify(order));
};

export const confirmPlacedOrder = () => {
  const confirmedOrder: ConfirmedOrder = {
    order: readJson<PlacedOrder>(PLACED_ORDER_KEY),
  };

  sessionStorage.removeItem(PLACED_ORDER_KEY);
  sessionStorage.setItem(CONFIRMED_ORDER_KEY, JSON.stringify(confirmedOrder));
};

export const getConfirmedOrder = () =>
  readJson<ConfirmedOrder>(CONFIRMED_ORDER_KEY);

export const clearConfirmedOrder = () => {
  sessionStorage.removeItem(CONFIRMED_ORDER_KEY);
};

// Where a return from the payment provider lands: the thank-you page when the
// payment was taken, the basket otherwise so the buyer can retry.
export const resolvePaymentReturn = (status: string | null): string => {
  // The payment flow has reached its end, so the "back from payment" redirect
  // on the order page should no longer trigger.
  sessionStorage.removeItem(PAYMENT_IN_PROGRESS_KEY);

  if (status && PAYMENT_SUCCESS_STATUSES.includes(status)) {
    confirmPlacedOrder();

    return routes.orderSuccess;
  }

  return routes.checkout;
};
